import { useContext, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Navigation, Pagination } from "swiper";
import "swiper/modules/navigation/navigation.scss";
import "swiper/modules/pagination/pagination.scss";
import { Swiper, SwiperSlide } from "swiper/react/swiper-react.js";
import "swiper/swiper.scss";
import STSwiper from "../../assets/styles/molecules/swiper.module.scss";
import STRecommendBoardsList from "../../assets/styles/organisms/recommendBoardsList.module.scss";
import { useSwiperSettings } from "../../hooks/useSwiperSettings";
import { SiteContext } from "../../providers/SiteProvider";
import { routes } from "../../router/Router";
import { fetchBoardsRecent } from "../../utils/api/fetchRecommendBoards";
import { GetBoardRecentResponse } from "../../api/board";
import BoardCard, { BoardItem } from "../molecules/BoardCard";
import CreateBoardOnTopPage from "./CreateBoardOnTopPage";

const RecommendBoardList: React.VFC = () => {
  const [recommendBoards, setRecommendBoards] = useState<BoardItem[]>([]);
  const [totalCategories, setTotalCategories] = useState<number>(0);
  const history = useHistory();
  const site = useContext(SiteContext);
  const mainColor = site.mainColor;
  const allowForumFeature = site.allowForumFeature;

  const breakpoints = {
    default: {
      slidesPerView: 4,
      slidesPerGroup: 2,
      centeredSlides: false,
      loopThreshold: 4,
    },
    sp: {
      slidesPerView: window.innerWidth < 400 ? 1.8 : 1.8,
      slidesPerGroup: 1,
      centeredSlides: false,
      loopThreshold: 2,
    },
  };

  const { shouldLoop, slidesPerView, slidesPerGroup, centeredSlides } =
    useSwiperSettings(recommendBoards.length, breakpoints);
  useEffect(() => {
    fetchBoardsRecent().then((res: GetBoardRecentResponse) => {
      if (!res.data) return history.push(routes.error.path);
      const totalCategories = res?.data?.total_categories ?? 0;
      const newBoards: BoardItem[] = res.data.data.map((res: any) => {
        return {
          nickname: res.nickname,
          avatarUrl: res.avatar_url,
          id: res.id,
          title: res.title,
          content: res.content,
          createdAt: res.created_at,
          likeCount: res.like_count,
          likeFlag: res.like_flag,
          commentCount: res.comment_count,
          imageUrls: res.image_urls,
          categoryId: res.category_id,
          commentFlag: res.comment_flag,
        };
      });
      setRecommendBoards(newBoards);
      setTotalCategories(totalCategories);
    });
  }, []);

  const renderingEmptyComponent =
    recommendBoards.length === 0 && !totalCategories;

  const renderingCreateNewBoardOnPage =
    recommendBoards.length === 0 && allowForumFeature && totalCategories > 0;

  if (renderingEmptyComponent) {
    return <></>;
  }

  return (
    <section className={STRecommendBoardsList.root_new}>
      <div className={STRecommendBoardsList.wrap}>
        <div className={STRecommendBoardsList.title_wrapper}>
          <div className={STRecommendBoardsList.title_new}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke={mainColor}
                stroke-width="6"
              />
            </svg>
            <h3>掲示板</h3>
          </div>

          <Link
            to={"/message-board/categories"}
            className={STRecommendBoardsList.link_to_all}
          >
            <h3>もっと見る</h3>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <mask
                id="mask0_202_1896"
                maskUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="16"
                height="16"
              >
                <rect width="16" height="16" fill="#D9D9D9" />
              </mask>
              <g mask="url(#mask0_202_1896)">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M5.29289 3.29289C5.68342 2.90237 6.31658 2.90237 6.70711 3.29289L10.7071 7.29289C11.0976 7.68342 11.0976 8.31658 10.7071 8.70711L6.70711 12.7071C6.31658 13.0976 5.68342 13.0976 5.29289 12.7071C4.90237 12.3166 4.90237 11.6834 5.29289 11.2929L8.58579 8L5.29289 4.70711C4.90237 4.31658 4.90237 3.68342 5.29289 3.29289Z"
                  fill="#222222"
                />
              </g>
            </svg>
          </Link>
        </div>
        {renderingCreateNewBoardOnPage ? (
          <CreateBoardOnTopPage />
        ) : (
          <div className={STRecommendBoardsList.swiper}>
            <Swiper
              modules={[Navigation, Pagination]}
              loop={false}
              spaceBetween={8}
              slidesPerView={slidesPerView}
              slidesPerGroup={slidesPerGroup}
              centeredSlides={centeredSlides}
              loopFillGroupWithBlank={!shouldLoop}
              pagination={{
                clickable: true,
                el: `.${STSwiper.pagination_boards}`,
                bulletActiveClass: STSwiper.pagination_bullet_active_boards,
                bulletClass: STSwiper.pagination_bullet_boards,
              }}
              navigation={
                shouldLoop
                  ? {
                      nextEl: `.${STSwiper.button_next_boards_new}`,
                      prevEl: `.${STSwiper.button_prev_boards_new}`,
                    }
                  : {}
              }
            >
              {recommendBoards.map((board) => (
                <SwiperSlide key={`slide-${board.id}`}>
                  <BoardCard boardDetail={board} />
                </SwiperSlide>
              ))}
              <SwiperSlide key={"next-button"}>
                <div className={STRecommendBoardsList.next_button}>
                  <p
                    className={STRecommendBoardsList.button_text}
                    onClick={() => {
                      window.location.href = "/message-board/categories";
                    }}
                  >
                    続きを見る &gt;&gt;
                  </p>
                </div>
              </SwiperSlide>
            </Swiper>
            <div className={STSwiper.pagination_boards}></div>
            <div className={STSwiper.button_prev_boards_new}></div>
            <div className={STSwiper.button_next_boards_new}></div>
          </div>
        )}
      </div>
      <Link
        to={"/message-board/categories"}
        className={STRecommendBoardsList.button_link}
      >
        <h3>掲示板一覧</h3>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <mask
            id="mask0_202_1896"
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="16"
            height="16"
          >
            <rect width="16" height="16" fill="#D9D9D9" />
          </mask>
          <g mask="url(#mask0_202_1896)">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M5.29289 3.29289C5.68342 2.90237 6.31658 2.90237 6.70711 3.29289L10.7071 7.29289C11.0976 7.68342 11.0976 8.31658 10.7071 8.70711L6.70711 12.7071C6.31658 13.0976 5.68342 13.0976 5.29289 12.7071C4.90237 12.3166 4.90237 11.6834 5.29289 11.2929L8.58579 8L5.29289 4.70711C4.90237 4.31658 4.90237 3.68342 5.29289 3.29289Z"
              fill="#222222"
            />
          </g>
        </svg>
      </Link>
    </section>
  );
};
export default RecommendBoardList;
